/** FCFS_COUPON 스케줄 · phase · 잔여 · 카드 copy/status 순수 유틸. */
import React from 'react';
import dayjs from 'dayjs';
import { ICouponInfo } from '@wconcept/wconcept-common/models/event/custom';
import {
  PromotionFcfsCouponDaySchedule,
  PromotionFcfsCouponModule,
  PromotionFcfsCouponRound,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import numberToKorean from '@wconcept/wconcept-common/utils/numberToKorean';

/** 소진 노티스 헤드라인(다음 회차 안내와 결합 시 사용). */
const FCFS_NOTICE_EXHAUSTED_HEADLINE = '쿠폰이 모두 소진 되었어요.';
/** 회차 간 대기 구간 전용 헤드라인(직전 회차 종료 후, 다음 회차까지 1시간 초과). */
const FCFS_NOTICE_EXHAUSTED_INTER_ROUND_GAP_HEADLINE =
  '쿠폰이 모두 소진 되었습니다.';

/**
 * 헤드라인 + 둘째 줄. 문자열에 `<br>` 넣어 `sanitizeContent` 거치면
 * 서버·클라 정제 결과가 달라 하이드레이션이 깨질 수 있어 DOM 줄바꿈 사용.
 */
function fcfsNoticeBarJoinSecondLine(
  headline: string,
  secondLine: string
): React.ReactNode {
  return [
    `${headline.trimEnd()} `,
    React.createElement('br', { key: 'fcfs-notice-break' }),
    secondLine,
  ];
}

const FCFS_NOTICE_NO_MORE_ROUNDS: React.ReactNode = fcfsNoticeBarJoinSecondLine(
  '쿠폰이 모두 소진 되었어요.',
  '다음에 다시 만나요.'
);

/**
 * 다음 비중단 회차 시작 시각만 기준으로 한 프리뷰 창입니다.
 * `[nextIssuanceStart - 1시간, nextIssuanceStart)` (왼쪽 끝 포함, 시작 시점 제외). 직전 회차 종료 시각과 무관하며,
 * `resolveFcfsPhase`에서는 이 구간에 들어가면 진행 중 활성·중단 회차보다 우선해 `PREVIEW_D1H`로 둡니다.
 */
export function fcfsIsNextRoundPreviewOneHourWindow(
  nextIssuanceStart: dayjs.Dayjs,
  referenceNow: dayjs.Dayjs
): boolean {
  if (!nextIssuanceStart.isValid() || !referenceNow.isValid()) {
    return false;
  }
  const startMs = nextIssuanceStart.valueOf();
  const refMs = referenceNow.valueOf();
  const windowOpenInclusiveMs = nextIssuanceStart.subtract(1, 'hour').valueOf();
  return refMs >= windowOpenInclusiveMs && refMs < startMs;
}

/** FCFS 노티·페이즈의 «오늘/내일/월일» 계산 전용 타임존(서비스 기준 통일로 SSR hydrate 불일치 방지). */
const FCFS_CALENDAR_ZONE = 'Asia/Seoul';

let fcfsSeoulYmdFormatter: Intl.DateTimeFormat | undefined;

function fcfsMomentToSeoulCalendarDayKey(moment: dayjs.Dayjs): string {
  if (!fcfsSeoulYmdFormatter) {
    fcfsSeoulYmdFormatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: FCFS_CALENDAR_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  }
  return fcfsSeoulYmdFormatter.format(moment.toDate());
}

/** CMS `HH:mm` 또는 `HH:mm:ss` 등을 `HH:mm:ss`로 고정(RFC3339 오프셋 결합 편의). */
function fcfsNormalizeWallClockToHms(timeRaw: string): string | null {
  const trimmed = String(timeRaw ?? '')
    .trim()
    .split('.')[0]
    .trim();
  const matched = trimmed.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!matched) {
    return null;
  }
  const hour = Number.parseInt(matched[1], 10);
  const minute = Number.parseInt(matched[2], 10);
  const second = matched[3] !== undefined ? Number.parseInt(matched[3], 10) : 0;
  if (
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second) ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59 ||
    second < 0 ||
    second > 59
  ) {
    return null;
  }
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(hour)}:${pad(minute)}:${pad(second)}`;
}

/**
 * 회차·운영일의 「날짜 + 벽시각」을 **항상 Seoul** 기준 절대시각으로 파싱합니다.
 * (`dayjs(`${date}T${time}`)`는 런타임 TZ에 따라 순간값이 달라집니다.)
 */
function fcfsMomentFromSeoulWallClock(
  yyyyMmDd: string,
  timeRaw: string
): dayjs.Dayjs | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(yyyyMmDd)) {
    return null;
  }
  const hms = fcfsNormalizeWallClockToHms(timeRaw);
  if (hms === null) {
    return null;
  }
  const parsed = dayjs(`${yyyyMmDd}T${hms}+09:00`);
  return parsed.isValid() ? parsed : null;
}

let fcfsSeoulHmPartsFormatter: Intl.DateTimeFormat | undefined;

/** 노티·카드 시각 카피: 로컬 런타임이 아니라 Seoul 벽시계 시·분. */
function fcfsMomentToSeoulHourAndMinute(moment: dayjs.Dayjs): {
  hour24: number;
  minute: number;
} | null {
  const instant = moment.toDate();
  if (!Number.isFinite(instant.getTime())) {
    return null;
  }
  if (!fcfsSeoulHmPartsFormatter) {
    fcfsSeoulHmPartsFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: FCFS_CALENDAR_ZONE,
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });
  }
  let hour24 = NaN;
  let minute = NaN;
  for (const token of fcfsSeoulHmPartsFormatter.formatToParts(instant)) {
    if (token.type === 'hour') hour24 = Number.parseInt(token.value, 10);
    if (token.type === 'minute') minute = Number.parseInt(token.value, 10);
  }
  if (
    !Number.isFinite(hour24) ||
    !Number.isFinite(minute) ||
    hour24 < 0 ||
    hour24 > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }
  return { hour24, minute };
}

/** Seoul 달력 `YYYY-MM-DD`에 서울 달력일을 더함(내일 분기 등). */
function fcfsAddSeoulCalendarDaysFromDayKey(
  dayKeyYmd: string,
  daysToAdd: number
): string {
  return dayjs(`${dayKeyYmd}T12:00:00+09:00`)
    .add(daysToAdd, 'day')
    .format('YYYY-MM-DD');
}

export const FCFS_COUPON_EVENT_ENDED_DESC = '발급 종료';
export const FCFS_COUPON_STOCK_CHECKING_DESC = '쿠폰 정보 확인 중';
/** 대기 회차 카드 서브 카피(`PREVIEW_D1H`·`BETWEEN_*`). */
export const FCFS_COUPON_SCHEDULED_LABEL = '발급 예정';

export interface FcfsRoundEntry {
  schedule: PromotionFcfsCouponDaySchedule;
  round: PromotionFcfsCouponRound;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
}

export interface FcfsRoundRefs {
  active: FcfsRoundEntry | null;
  next: FcfsRoundEntry | null;
  issuanceWindow: FcfsRoundEntry | null;
  lastPast: FcfsRoundEntry | null;
}

export type FcfsPhase =
  | 'BEFORE_EVENT'
  | 'AFTER_EVENT'
  | 'CURRENT_ROUND_SUSPENDED'
  | 'ACTIVE'
  | 'PREVIEW_D1H'
  | 'BETWEEN_TODAY'
  | 'BETWEEN_TOMORROW'
  | 'EVENT_ENDED'
  | 'NONE';

export type FcfsNoticeType =
  | 'before'
  | 'preview'
  | 'active'
  | 'between'
  | 'suspended'
  | 'end';

export interface FcfsNoticeBar {
  text: React.ReactNode;
  type: FcfsNoticeType;
}

export interface FcfsPhaseContext {
  module: PromotionFcfsCouponModule;
  active: FcfsRoundEntry | null;
  next: FcfsRoundEntry | null;
  issuanceWindow: FcfsRoundEntry | null;
  lastPast: FcfsRoundEntry | null;
}

/** 프로모션(`PromotionInfo`) 전시 시작·종료 — FCFS 행사 전·후 판정에 사용. */
export interface FcfsPromotionDisplayBounds {
  displayStartDate: string;
  displayEndDate: string;
}

/**
 * 프로모션 전시 기간 밖 여부.
 * 다른 모듈의 `displayTimeChecker.getDisplayStatus` / `isInDisplayTimeRange`와 동일:
 * 활성 구간은 `start <= now < end`.
 */
export function resolveFcfsPhaseByPromotionBounds(
  now: dayjs.Dayjs,
  promotionDisplayBounds?: FcfsPromotionDisplayBounds | null
): 'BEFORE_EVENT' | 'AFTER_EVENT' | 'IN_WINDOW' | 'SKIP' {
  const startRaw = promotionDisplayBounds?.displayStartDate?.trim() ?? '';
  const endRaw = promotionDisplayBounds?.displayEndDate?.trim() ?? '';
  if (!startRaw || !endRaw) return 'SKIP';
  const startMs = new Date(startRaw).getTime();
  const endMs = new Date(endRaw).getTime();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
    return 'SKIP';
  }
  const timestamp = now.valueOf();
  if (timestamp < startMs) return 'BEFORE_EVENT';
  if (timestamp >= endMs) return 'AFTER_EVENT';
  return 'IN_WINDOW';
}

export type FcfsCouponItemStatus =
  | 'download'
  | 'comingsoon'
  | 'end'
  | 'soldout';

export const isFcfsRoundSuspended = (
  round: PromotionFcfsCouponRound
): boolean => {
  const flag = round.suspended as unknown;
  return flag === true || flag === 'true' || flag === 1 || flag === '1';
};

/** 운영 시작 전·종료 후·NONE 은 비표시. EVENT_ENDED 는 노출. */
export const isFcfsPublicDisplayPhase = (phase: FcfsPhase): boolean =>
  phase !== 'NONE' && phase !== 'BEFORE_EVENT' && phase !== 'AFTER_EVENT';

/** 회차 캘린더일 정규화: CMS가 `YYYY.MM.DD` 등으로 줄 때 `fcfsMomentToSeoulCalendarDayKey`(하이픈)와 비교가 깨지지 않게 한다. */
export function fcfsNormalizeScheduleDateToDayKey(
  dateRaw: string | undefined | null
): string | null {
  if (dateRaw == null) return null;
  const trimmed = String(dateRaw).trim();
  if (!trimmed) return null;
  const digitsOnly = trimmed.replace(/\D/g, '');
  if (digitsOnly.length === 8 && /^\d{8}$/.test(digitsOnly)) {
    return `${digitsOnly.slice(0, 4)}-${digitsOnly.slice(
      4,
      6
    )}-${digitsOnly.slice(6, 8)}`;
  }
  const withHyphens = trimmed.replace(/\./g, '-').replace(/\//g, '-');
  const parsed = dayjs(withHyphens);
  if (!parsed.isValid()) {
    return null;
  }
  return parsed.format('YYYY-MM-DD');
}

/**
 * 회차 경계는 분 단위(start=:00, end=:59.999).
 * 날짜·시각은 **Seoul 벽시계 + KST(+09:00)** 로 고정해 Node(UTC)·브라우저에서 동일 순간값으로 맞춥니다.
 */
export const combineDateTime = (
  date: string,
  time: string,
  boundary?: 'start' | 'end'
): dayjs.Dayjs => {
  const normalizedDayKey = fcfsNormalizeScheduleDateToDayKey(date);
  const datePart =
    normalizedDayKey ??
    String(date).trim().replace(/\./g, '-').replace(/\//g, '-');
  const seoulMoment = fcfsMomentFromSeoulWallClock(datePart, time);
  const base =
    seoulMoment !== null ? seoulMoment : dayjs(`${datePart}T${time}`);
  if (boundary === 'start') {
    return base.second(0).millisecond(0);
  }
  if (boundary === 'end') {
    return base.second(59).millisecond(999);
  }
  return base;
};

export const flattenRounds = (
  dayScheduleList: PromotionFcfsCouponDaySchedule[] | undefined | null
): FcfsRoundEntry[] => {
  if (!dayScheduleList || dayScheduleList.length === 0) {
    return [];
  }

  const entryList: FcfsRoundEntry[] = [];

  for (const schedule of dayScheduleList) {
    if (schedule?.roundList?.length) {
      for (const round of schedule.roundList) {
        entryList.push({
          schedule,
          round,
          start: combineDateTime(
            schedule.scheduleDate,
            round.startTime,
            'start'
          ),
          end: combineDateTime(schedule.scheduleDate, round.endTime, 'end'),
        });
      }
    }
  }

  entryList.sort((a, b) => a.start.valueOf() - b.start.valueOf());
  return entryList;
};

export const getEarliestFcfsRound = (
  dayScheduleList: PromotionFcfsCouponDaySchedule[] | undefined | null
): FcfsRoundEntry | null => {
  const entryList = flattenRounds(dayScheduleList);
  return entryList.length > 0 ? entryList[0] : null;
};

export const findIssuanceWindowRound = (
  dayScheduleList: PromotionFcfsCouponDaySchedule[] | undefined | null,
  now: dayjs.Dayjs = dayjs()
): FcfsRoundEntry | null => {
  const entryList = flattenRounds(dayScheduleList);
  for (const entry of entryList) {
    if (
      (now.isSame(entry.start) || now.isAfter(entry.start)) &&
      now.isBefore(entry.end)
    ) {
      return entry;
    }
  }
  return null;
};

export const findActiveRound = (
  dayScheduleList: PromotionFcfsCouponDaySchedule[] | undefined | null,
  now: dayjs.Dayjs = dayjs()
): FcfsRoundEntry | null => {
  const entryList = flattenRounds(dayScheduleList);
  for (const entry of entryList) {
    if (!isFcfsRoundSuspended(entry.round)) {
      if (
        (now.isSame(entry.start) || now.isAfter(entry.start)) &&
        now.isBefore(entry.end)
      ) {
        return entry;
      }
    }
  }
  return null;
};

export const findLastPastRound = (
  dayScheduleList: PromotionFcfsCouponDaySchedule[] | undefined | null,
  now: dayjs.Dayjs = dayjs()
): FcfsRoundEntry | null => {
  const entryList = flattenRounds(dayScheduleList);
  let last: FcfsRoundEntry | null = null;
  for (const entry of entryList) {
    if (entry.end.isBefore(now) || entry.end.isSame(now)) {
      if (!last || entry.end.isAfter(last.end)) {
        last = entry;
      }
    }
  }
  return last;
};

export const findNextRound = (
  dayScheduleList: PromotionFcfsCouponDaySchedule[] | undefined | null,
  now: dayjs.Dayjs = dayjs()
): FcfsRoundEntry | null => {
  const entryList = flattenRounds(dayScheduleList);
  for (const entry of entryList) {
    if (!isFcfsRoundSuspended(entry.round) && entry.start.isAfter(now)) {
      return entry;
    }
  }
  return null;
};

export function resolveFcfsRoundRefs(
  dayScheduleList: PromotionFcfsCouponDaySchedule[] | undefined | null,
  now: dayjs.Dayjs = dayjs()
): FcfsRoundRefs {
  return {
    active: findActiveRound(dayScheduleList, now),
    next: findNextRound(dayScheduleList, now),
    issuanceWindow: findIssuanceWindowRound(dayScheduleList, now),
    lastPast: findLastPastRound(dayScheduleList, now),
  };
}

export const resolveFcfsPhase = (
  module: PromotionFcfsCouponModule,
  now: dayjs.Dayjs = dayjs(),
  promotionDisplayBounds?: FcfsPromotionDisplayBounds | null
): FcfsPhase => {
  if (!module) {
    return 'NONE';
  }

  const promotionEdge = resolveFcfsPhaseByPromotionBounds(
    now,
    promotionDisplayBounds
  );

  if (promotionEdge === 'BEFORE_EVENT') {
    return 'BEFORE_EVENT';
  }
  if (promotionEdge === 'AFTER_EVENT') {
    return 'AFTER_EVENT';
  }

  if (promotionEdge === 'SKIP') {
    const operationStartRawKey =
      fcfsNormalizeScheduleDateToDayKey(module.operationStartDate) ??
      String(module.operationStartDate ?? '')
        .trim()
        .replace(/\./g, '-')
        .replace(/\//g, '-');
    const operationEndRawKey =
      fcfsNormalizeScheduleDateToDayKey(module.operationEndDate) ??
      String(module.operationEndDate ?? '')
        .trim()
        .replace(/\./g, '-')
        .replace(/\//g, '-');
    const operationStart =
      fcfsMomentFromSeoulWallClock(operationStartRawKey, '00:00:00') ??
      dayjs(`${module.operationStartDate}T00:00:00`).second(0).millisecond(0);
    const operationEnd =
      fcfsMomentFromSeoulWallClock(operationEndRawKey, '23:59:59') ??
      dayjs(`${module.operationEndDate}T23:59:59`).second(59).millisecond(999);
    if (now.isBefore(operationStart)) {
      return 'BEFORE_EVENT';
    }
    if (now.isAfter(operationEnd)) {
      return 'AFTER_EVENT';
    }
  }

  const issuanceWindow = findIssuanceWindowRound(module.dayScheduleList, now);
  const active = findActiveRound(module.dayScheduleList, now);
  const next = findNextRound(module.dayScheduleList, now);

  const inNextRoundPreviewHourWindow =
    next !== null && fcfsIsNextRoundPreviewOneHourWindow(next.start, now);

  /**
   * 다음 비중단 회차 `start` 기준 정확히 1시간 전([start-1h, start))부터는 무조건 프리뷰.
   * 직전 종료 시각과 무관하며, `findActiveRound` 진행 여부와도 무관(진행 회차 중이어도 동일 구간에서는 프리뷰 우선).
   */
  if (inNextRoundPreviewHourWindow) {
    return 'PREVIEW_D1H';
  }

  if (issuanceWindow !== null && isFcfsRoundSuspended(issuanceWindow.round)) {
    return 'CURRENT_ROUND_SUSPENDED';
  }

  if (active !== null) {
    return 'ACTIVE';
  }

  if (next !== null) {
    const today = fcfsMomentToSeoulCalendarDayKey(now);
    const nextCalendarDayKey =
      fcfsNormalizeScheduleDateToDayKey(next.schedule.scheduleDate) ??
      fcfsMomentToSeoulCalendarDayKey(next.start);
    if (nextCalendarDayKey === today) {
      return 'BETWEEN_TODAY';
    }
    if (nextCalendarDayKey > today) {
      return 'BETWEEN_TOMORROW';
    }
    return 'NONE';
  }

  const lastPast = findLastPastRound(module.dayScheduleList, now);
  if (lastPast) {
    return 'EVENT_ENDED';
  }

  return 'NONE';
};

/**
 * 직전 회차 종료 후 ~ 다음 회차 시작 전(발급 대기 구간으로 `BETWEEN_TODAY` / `BETWEEN_TOMORROW`일 때만).
 * 당일 첫 회차 시작 전처럼 `lastPast`가 없거나 다른 날 캘린더 정렬에서는 false.
 */
export function fcfsIsBetweenRoundsEndedGapScenario(
  phase: FcfsPhase,
  lastPast: FcfsRoundEntry | null,
  next: FcfsRoundEntry | null,
  referenceNow: dayjs.Dayjs
): boolean {
  if (phase !== 'BETWEEN_TODAY' && phase !== 'BETWEEN_TOMORROW') return false;
  if (!lastPast || !next) return false;
  if (!lastPast.end.isBefore(next.start)) return false;

  const refDay = fcfsMomentToSeoulCalendarDayKey(referenceNow);
  const lastPastDayKey =
    fcfsNormalizeScheduleDateToDayKey(lastPast.schedule.scheduleDate) ??
    fcfsMomentToSeoulCalendarDayKey(lastPast.start);
  const nextDayKey =
    fcfsNormalizeScheduleDateToDayKey(next.schedule.scheduleDate) ??
    fcfsMomentToSeoulCalendarDayKey(next.start);

  if (
    phase === 'BETWEEN_TODAY' &&
    lastPastDayKey === refDay &&
    lastPastDayKey === nextDayKey
  ) {
    return true;
  }

  if (
    phase === 'BETWEEN_TOMORROW' &&
    lastPastDayKey === refDay &&
    nextDayKey !== refDay
  ) {
    return true;
  }

  return false;
}

export function resolveFcfsBetweenRoundsEndedGapDisplayRound(
  phase: FcfsPhase,
  lastPast: FcfsRoundEntry | null,
  next: FcfsRoundEntry | null,
  referenceNow: dayjs.Dayjs
): FcfsRoundEntry | null {
  return fcfsIsBetweenRoundsEndedGapScenario(
    phase,
    lastPast,
    next,
    referenceNow
  )
    ? lastPast
    : null;
}

export function resolveFcfsDisplayRound(
  rawPhase: FcfsPhase,
  dayScheduleList: PromotionFcfsCouponDaySchedule[] | undefined | null,
  roundRefs: FcfsRoundRefs,
  referenceNow: dayjs.Dayjs = dayjs()
): FcfsRoundEntry | null {
  const { active, next, issuanceWindow, lastPast } = roundRefs;
  switch (rawPhase) {
    case 'ACTIVE':
      return active;
    case 'PREVIEW_D1H':
      return next;
    case 'CURRENT_ROUND_SUSPENDED':
      return issuanceWindow;
    case 'BETWEEN_TODAY':
    case 'BETWEEN_TOMORROW': {
      const gapRound = resolveFcfsBetweenRoundsEndedGapDisplayRound(
        rawPhase,
        lastPast,
        next,
        referenceNow
      );
      if (gapRound) return gapRound;
      return next ?? getEarliestFcfsRound(dayScheduleList);
    }
    case 'EVENT_ENDED':
      return lastPast;
    default:
      return null;
  }
}

export function collectRoundCouponCdList(
  round: PromotionFcfsCouponRound
): string[] {
  const fromItemList =
    round.couponItemList
      ?.map(item => item.couponCd)
      .filter(
        (couponCd): couponCd is string =>
          couponCd != null && String(couponCd).trim() !== ''
      ) ?? [];
  if (fromItemList.length > 0) {
    return [...new Set(fromItemList)];
  }
  const fromEntryList =
    round.couponEntryList
      ?.map(entry => entry?.couponCd)
      .filter(
        (couponCd): couponCd is string =>
          couponCd != null && String(couponCd).trim() !== ''
      ) ?? [];
  return [...new Set(fromEntryList)];
}

export function isWholeRoundSoldOut(
  roundEntry: FcfsRoundEntry | null,
  remainingByCd: Record<string, number> | undefined | null
): boolean {
  if (!roundEntry || !remainingByCd) return false;
  const couponCdList = collectRoundCouponCdList(roundEntry.round);
  if (couponCdList.length === 0) return false;
  for (const couponCd of couponCdList) {
    if (!(couponCd in remainingByCd)) return false;
    if (remainingByCd[couponCd] !== 0) return false;
  }
  return true;
}

export function isCardSoldOut(
  couponCd: string,
  remainingByCd: Record<string, number> | undefined | null
): boolean {
  if (!remainingByCd) return false;
  const trimmed = couponCd.trim();
  if (!(trimmed in remainingByCd)) return false;
  return remainingByCd[trimmed] === 0;
}

export function resolveFcfsPhaseWithRemaining(
  rawPhase: FcfsPhase,
  displayRound: FcfsRoundEntry | null,
  remainingByCd: Record<string, number> | undefined,
  module: PromotionFcfsCouponModule,
  now: dayjs.Dayjs
): FcfsPhase {
  const soldOutDuringActiveRound =
    rawPhase === 'ACTIVE' &&
    displayRound !== null &&
    isWholeRoundSoldOut(displayRound, remainingByCd);

  if (soldOutDuringActiveRound) {
    const nextRound = findNextRound(module.dayScheduleList, now);
    const inPreviewWindowBeforeNextRound =
      nextRound !== null &&
      fcfsIsNextRoundPreviewOneHourWindow(nextRound.start, now);
    if (inPreviewWindowBeforeNextRound) {
      return 'PREVIEW_D1H';
    }
    return 'CURRENT_ROUND_SUSPENDED';
  }
  return rawPhase;
}

/**
 * `resolveFcfsDisplayRound`에 넘길 phase — 재고 소진 후에도 카드 회차(active) 추적이 필요하면 raw 유지.
 */
export function resolveFcfsPhaseKeyForDisplayRound(
  rawPhase: FcfsPhase,
  phaseAfterRemaining: FcfsPhase
): FcfsPhase {
  if (phaseAfterRemaining === 'PREVIEW_D1H') {
    return 'PREVIEW_D1H';
  }
  if (
    phaseAfterRemaining === 'CURRENT_ROUND_SUSPENDED' &&
    rawPhase === 'ACTIVE'
  ) {
    return 'ACTIVE';
  }
  return rawPhase;
}

export function computeFcfsShowCheckingUi(params: {
  isClientReady: boolean;
  rawPhase: FcfsPhase;
  couponCdListLength: number;
  isFcfsRemainingError: boolean;
  remainingByCd: Record<string, number> | undefined;
  isFcfsRemainingPending: boolean;
}): boolean {
  const needsRemainingForActive =
    params.rawPhase === 'ACTIVE' &&
    params.couponCdListLength > 0 &&
    !params.isFcfsRemainingError;
  const isRemainingResolved =
    params.remainingByCd !== undefined && !params.isFcfsRemainingPending;

  return (
    !params.isClientReady || (needsRemainingForActive && !isRemainingResolved)
  );
}

export const formatFcfsKoreanMd = (moment: dayjs.Dayjs): string => {
  const key = fcfsMomentToSeoulCalendarDayKey(moment);
  const monthPart = Number(key.slice(5, 7));
  const dayPart = Number(key.slice(8, 10));
  return `${monthPart}월 ${dayPart}일`;
};

export const formatFcfsKoreanHm = (moment: dayjs.Dayjs): string => {
  const seoulHm = fcfsMomentToSeoulHourAndMinute(moment);
  const hour24 = seoulHm?.hour24 ?? moment.hour();
  const minute = seoulHm?.minute ?? moment.minute();
  const isPm = hour24 >= 12;
  const twelveHour = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const dayPart = isPm ? '오후' : '오전';
  if (minute === 0) {
    return `${dayPart} ${twelveHour}시`;
  }
  return `${dayPart} ${twelveHour}시 ${minute}분`;
};

/**
 * 노티스바: 행사 시작 전·프리뷰·당일/익일 대기 등 — 당일이면 시각만,
 * **기준일 다음날**(캘린더 기준 내일, Seoul)이면 「내일 {시각}」, 그 외는 월·일 + 시각 +
 * 「부터 발급 받을 수 있어요.」(달력 비교는 `Asia/Seoul`로 SSR/클라이언트 간 동일 결과 보장.)
 */
export function formatFcfsNoticeIssuanceStartsLine(
  issuanceStart: dayjs.Dayjs,
  referenceNow: dayjs.Dayjs = dayjs()
): string {
  const hm = formatFcfsKoreanHm(issuanceStart);
  const startDayKey = fcfsMomentToSeoulCalendarDayKey(issuanceStart);
  const referenceDayKey = fcfsMomentToSeoulCalendarDayKey(referenceNow);
  if (startDayKey === referenceDayKey) {
    return `${hm}부터 발급 받을 수 있어요.`;
  }
  const tomorrowDayKey = fcfsAddSeoulCalendarDaysFromDayKey(referenceDayKey, 1);
  if (startDayKey === tomorrowDayKey) {
    return `내일 ${hm}부터 발급 받을 수 있어요.`;
  }
  return `${formatFcfsKoreanMd(issuanceStart)} ${hm}부터 발급 받을 수 있어요.`;
}

/**
 * 노티스바: 회차 소진(중단)·다음 회차 안내 접미.
 * 당일이면 시각만, 바로 다음 캘린더 일이면 「내일」, 그 외는 월·일 + 시각 — 「에 다시 만나요.」
 */
export function formatFcfsNoticeVisitAgainAfterExhausted(
  nextStart: dayjs.Dayjs,
  referenceNow: dayjs.Dayjs = dayjs()
): string {
  const hm = formatFcfsKoreanHm(nextStart);
  const nextDayKey = fcfsMomentToSeoulCalendarDayKey(nextStart);
  const referenceDayKey = fcfsMomentToSeoulCalendarDayKey(referenceNow);
  if (nextDayKey === referenceDayKey) {
    return `${hm}에 다시 만나요.`;
  }
  const tomorrowDayKey = fcfsAddSeoulCalendarDaysFromDayKey(referenceDayKey, 1);
  if (nextDayKey === tomorrowDayKey) {
    return `내일 ${hm}에 다시 만나요.`;
  }
  return `${formatFcfsKoreanMd(nextStart)} ${hm}에 다시 만나요.`;
}

/**
 * 카드 서브 카피(카운트다운·시각 등)에서 연속 숫자만 굵게.
 * CouponItem2 `.desc span` 규칙과 맞춤.
 */
export function fcfsBoldNumericDescParts(label: string): React.ReactNode {
  if (!label) return '';
  if (!/\d/.test(label)) return label;
  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let slotIndex = 0;

  for (const match of label.matchAll(/\d+/g)) {
    const digitText = match[0];
    const start =
      typeof match.index === 'number'
        ? match.index
        : label.indexOf(digitText, lastIndex);
    if (start > lastIndex) {
      nodes.push(label.slice(lastIndex, start));
    }
    nodes.push(
      React.createElement(
        'span',
        { key: `fcfs-bold-num-${slotIndex}` },
        digitText
      )
    );
    slotIndex += 1;
    lastIndex = start + digitText.length;
  }
  if (lastIndex < label.length) {
    nodes.push(label.slice(lastIndex));
  }
  return nodes;
}

export const formatFcfsActiveCountdown = (
  end: dayjs.Dayjs,
  now: dayjs.Dayjs = dayjs()
): string => {
  const diffMs = end.valueOf() - now.valueOf();
  if (diffMs <= 0) return '0시간 0분 0초 남음';
  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}시간 ${minutes}분 ${seconds}초 남음`;
};

export const getFcfsNoticeBar = (
  phase: FcfsPhase,
  ctx: FcfsPhaseContext,
  referenceNow: dayjs.Dayjs = dayjs()
): FcfsNoticeBar | null => {
  const { module, next, issuanceWindow, lastPast } = ctx;

  const interRoundGapExhaustNotice = (): FcfsNoticeBar | null => {
    if (
      !fcfsIsBetweenRoundsEndedGapScenario(
        phase,
        lastPast,
        next,
        referenceNow
      ) ||
      !next
    ) {
      return null;
    }
    return {
      text: fcfsNoticeBarJoinSecondLine(
        FCFS_NOTICE_EXHAUSTED_INTER_ROUND_GAP_HEADLINE,
        formatFcfsNoticeVisitAgainAfterExhausted(next.start, referenceNow)
      ),
      type: 'between',
    };
  };

  switch (phase) {
    case 'BEFORE_EVENT': {
      const earliest = getEarliestFcfsRound(module.dayScheduleList);
      if (earliest) {
        return {
          text: formatFcfsNoticeIssuanceStartsLine(
            earliest.start,
            referenceNow
          ),
          type: 'before',
        };
      }
      const operationStartMdKey =
        fcfsNormalizeScheduleDateToDayKey(module.operationStartDate) ??
        String(module.operationStartDate ?? '')
          .trim()
          .replace(/\./g, '-')
          .replace(/\//g, '-');
      const op =
        fcfsMomentFromSeoulWallClock(operationStartMdKey, '00:00:00') ??
        dayjs(`${module.operationStartDate}T00:00:00`);
      return {
        text: `${formatFcfsKoreanMd(op)}부터 발급 받을 수 있어요.`,
        type: 'before',
      };
    }

    case 'CURRENT_ROUND_SUSPENDED': {
      if (!issuanceWindow) {
        return null;
      }
      if (!next) {
        return {
          text: FCFS_NOTICE_NO_MORE_ROUNDS,
          type: 'end',
        };
      }
      return {
        text: fcfsNoticeBarJoinSecondLine(
          FCFS_NOTICE_EXHAUSTED_HEADLINE,
          formatFcfsNoticeVisitAgainAfterExhausted(next.start, referenceNow)
        ),
        type: 'suspended',
      };
    }

    case 'PREVIEW_D1H':
      if (!next) {
        return null;
      }
      return {
        text: formatFcfsNoticeIssuanceStartsLine(next.start, referenceNow),
        type: 'preview',
      };

    case 'ACTIVE':
      return null;

    case 'BETWEEN_TODAY': {
      const gapNotice = interRoundGapExhaustNotice();
      if (gapNotice) return gapNotice;
      if (!next) {
        return null;
      }
      return {
        text: formatFcfsNoticeIssuanceStartsLine(next.start, referenceNow),
        type: 'between',
      };
    }

    case 'BETWEEN_TOMORROW': {
      const gapNoticeT = interRoundGapExhaustNotice();
      if (gapNoticeT) return gapNoticeT;
      if (!next) {
        return null;
      }
      return {
        text: formatFcfsNoticeIssuanceStartsLine(next.start, referenceNow),
        type: 'between',
      };
    }

    case 'EVENT_ENDED':
      return {
        text: FCFS_NOTICE_NO_MORE_ROUNDS,
        type: 'end',
      };

    case 'AFTER_EVENT':
      return {
        text: FCFS_NOTICE_NO_MORE_ROUNDS,
        type: 'end',
      };

    case 'NONE':
    default:
      return null;
  }
};

function fcfsDiscountSubtitle(coupon: ICouponInfo): string {
  if (!coupon.maxDiscountAmount) return '';
  return `최대 ${numberToKorean(coupon.maxDiscountAmount, 'mixed')}원 할인`;
}

export interface FcfsCouponItemPhaseOptions {
  /** 직전 회차 종료 후 대기 구간(다음 시작 1시간 초과) 카드·카피 처리 */
  betweenRoundsEndedGap?: boolean;
}

export function fcfsCouponItemStatus(
  phase: FcfsPhase,
  options?: FcfsCouponItemPhaseOptions
): FcfsCouponItemStatus {
  if (
    options?.betweenRoundsEndedGap &&
    (phase === 'BETWEEN_TODAY' || phase === 'BETWEEN_TOMORROW')
  ) {
    return 'soldout';
  }
  switch (phase) {
    case 'ACTIVE':
      return 'download';
    case 'CURRENT_ROUND_SUSPENDED':
      return 'soldout';
    case 'EVENT_ENDED':
      return 'end';
    case 'PREVIEW_D1H':
    case 'BETWEEN_TODAY':
    case 'BETWEEN_TOMORROW':
      return 'comingsoon';
    default:
      return 'download';
  }
}

/**
 * 카드 설명 문구. `PREVIEW_D1H`·`BETWEEN_*`(소진 간격 아님) 카드 서브 카피는 시각 문자열 없이 `FCFS_COUPON_SCHEDULED_LABEL`만 사용합니다.
 */
export function fcfsCouponDesc(
  phase: FcfsPhase,
  coupon?: ICouponInfo,
  activeFallback?: string,
  options?: FcfsCouponItemPhaseOptions
): string {
  if (phase === 'ACTIVE') {
    if (activeFallback) return activeFallback;
    return coupon ? fcfsDiscountSubtitle(coupon) : '';
  }
  if (
    options?.betweenRoundsEndedGap &&
    (phase === 'BETWEEN_TODAY' || phase === 'BETWEEN_TOMORROW')
  ) {
    return '수량 소진';
  }
  switch (phase) {
    case 'CURRENT_ROUND_SUSPENDED':
      return '수량 소진';
    case 'EVENT_ENDED':
      return FCFS_COUPON_EVENT_ENDED_DESC;
    case 'PREVIEW_D1H':
    case 'BETWEEN_TODAY':
    case 'BETWEEN_TOMORROW':
      return FCFS_COUPON_SCHEDULED_LABEL;
    default:
      return coupon ? fcfsDiscountSubtitle(coupon) : '';
  }
}

export function resolveFcfsCouponItemStatus(
  phase: FcfsPhase,
  couponCd: string | undefined,
  remainingByCd: Record<string, number> | undefined,
  options?: FcfsCouponItemPhaseOptions
): FcfsCouponItemStatus {
  if (
    phase === 'ACTIVE' &&
    couponCd &&
    isCardSoldOut(couponCd, remainingByCd)
  ) {
    return 'soldout';
  }
  return fcfsCouponItemStatus(phase, options);
}

export function resolveFcfsCouponItemDesc(
  phase: FcfsPhase,
  couponCd: string | undefined,
  coupon: ICouponInfo | undefined,
  remainingByCd: Record<string, number> | undefined,
  activeCountdownText: string,
  options?: FcfsCouponItemPhaseOptions
): string {
  if (
    phase === 'ACTIVE' &&
    couponCd &&
    isCardSoldOut(couponCd, remainingByCd)
  ) {
    return '수량 소진';
  }
  return fcfsCouponDesc(phase, coupon, activeCountdownText, options);
}

export function phaseAllowsEntryFallback(phase: FcfsPhase): boolean {
  switch (phase) {
    case 'CURRENT_ROUND_SUSPENDED':
    case 'EVENT_ENDED':
    case 'PREVIEW_D1H':
    case 'BETWEEN_TODAY':
    case 'BETWEEN_TOMORROW':
      return true;
    default:
      return false;
  }
}

export function fcfsFallbackRowReactKey(slot: string, ordinal: number): string {
  return slot === ''
    ? `fcfs-cd-fallback-empty-${ordinal}`
    : `${slot}-${ordinal}`;
}

export function fcfsFallbackEntryTitle(
  entryCount: number,
  index: number
): string {
  return entryCount > 1 ? `선착순 할인 쿠폰 ${index + 1}` : '선착순 할인 쿠폰';
}
